import React, { useLayoutEffect, useRef } from "react";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Tooltip,
  useTheme,
} from "@mui/material";
import { alpha, Theme } from "@mui/material/styles";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Link as RouterLink,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import {
  panelTitleSx,
  roundedPanelSx,
  workspaceSwitchSx,
} from "../../Dashboard/overviewPanel";
import dayjs from "dayjs";
import { isNative } from "../../../lib/platform";

export const investorTableSx = (theme: Theme) => ({
  "& .MuiTableCell-root": {
    px: 1.5,
    py: 1,
    borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.07)}`,
    fontSize: "0.8125rem",
    fontVariantNumeric: "tabular-nums",
  },
  "& .MuiTableCell-head": {
    bgcolor: "transparent",
    color: "text.secondary",
    fontSize: "0.8125rem",
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    lineHeight: 1.5,
    py: 1,
  },
  "& .trade-value": {
    textAlign: "right",
  },
  "& tbody .trade-value": {
    fontSize: "0.8125rem",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
});
export const profileCardSx = (theme: Theme) => ({
  ...roundedPanelSx(theme.palette.mode === "dark"),
  color: "text.primary",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  justifyContent: "flex-start",
  textAlign: "left",
  height: "100%",
  width: "100%",
  transition:
    "border-color 180ms ease, background-color 180ms ease, transform 180ms ease",
  "&:hover": {
    borderColor: alpha(theme.palette.primary.main, 0.5),
    bgcolor: alpha(theme.palette.primary.main, 0.035),
    transform: "translateY(-2px)",
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 3,
  },
  "&.Mui-disabled": { opacity: 0.55 },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
    "&:hover": { transform: "none" },
  },
});

interface InvestorWorkspaceProps {
  children: React.ReactNode;
  tour?: string;
}
export const InvestorWorkspace = ({
  children,
  tour,
}: InvestorWorkspaceProps) => {
  const { pathname, search, key } = useLocation();
  const navigationType = useNavigationType();
  useLayoutEffect(() => {
    if (navigationType !== "POP")
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, search, key, navigationType]);
  return (
    <Box
      data-tour={tour}
      sx={{
        width: "100%",

        mx: "auto",
        minWidth: 0,
        px: { xs: 2, lg: 3 },
        pt: 0,
        pb: { xs: "calc(64px + env(safe-area-inset-bottom, 0px))", lg: 4 },
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
      }}
    >
      {children}
    </Box>
  );
};

interface WorkspaceHeaderProps {
  title: string;
  description?: string;
  titleAdornment?: React.ReactNode;
  backTo?: string;
  backLabel?: string;
  backState?: { investorSection: string };
  children?: React.ReactNode;
  navigation?: React.ReactNode;
  actions?: React.ReactNode;
  search?: React.ReactNode;
  metrics?: React.ReactNode;
  mobileProfile?: boolean;
  inlineMobileActions?: boolean;
}
export const WorkspaceHeader = ({
  title,
  description,
  titleAdornment,
  backTo,
  backLabel = "Back",
  backState,
  children,
  navigation,
  actions,
  search,
  metrics,
  mobileProfile = false,
  inlineMobileActions = false,
}: WorkspaceHeaderProps) => (
  <Box
    component="header"
    sx={{
      display: search ? "grid" : "flex",
      gridTemplateColumns: {
        xs: "minmax(0, 1fr) auto",
        lg: "minmax(0, 1fr) minmax(220px, 340px) minmax(0, 1fr)",
      },
      alignItems: "center",
      flexWrap: "wrap",
      columnGap: { xs: 1, lg: 2.5 },
      rowGap: 1.5,
      minHeight: { xs: 56, lg: 64 },
      py: 1,
      flexShrink: 0,
      px: { xs: 2, lg: 2.5 },
      mx: { xs: -2, lg: -3 },
      borderBottom: isNative() ? 0 : 1,
      borderColor: "divider",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: { xs: 1.5, sm: 1, lg: 1.5 },
        minWidth: 0,
        flex: { xs: "1 1 auto", sm: "1 1 0%", lg: "1 1 auto" },
        ...(inlineMobileActions && { flex: { xs: "1 1 0%", lg: "1 1 auto" } }),
        ...(mobileProfile && { width: { xs: "100%", sm: "auto" } }),
        ...(mobileProfile && {
          position: { xs: "relative", sm: "static" },
          justifyContent: { xs: "center", sm: "flex-start" },
          minHeight: { xs: 44, sm: "auto" },
          px: { xs: isNative() ? 0 : 5, sm: 0 },
        }),
      }}
    >
      {backTo && !isNative() && (
        <Tooltip title={backLabel}>
          <IconButton
            component={RouterLink}
            to={backTo}
            state={backState}
            aria-label={backLabel}
            sx={{
              width: 40,
              height: 44,
              flexShrink: 0,
              color: "text.secondary",
              ml: -1,
              ...(mobileProfile && {
                position: { xs: "absolute", sm: "static" },
                left: { xs: 0, sm: "auto" },
              }),
            }}
          >
            <ArrowLeft size={18} />
          </IconButton>
        </Tooltip>
      )}
      {children && (
        <Box
          sx={{
            flexShrink: 0,
            "& .MuiAvatar-root": { width: 32, height: 32, borderRadius: 1.5 },
          }}
        >
          {children}
        </Box>
      )}
      {navigation && (
        <Box sx={{ minWidth: 0, flex: 1 }} data-tour="super-investors-tabs">
          {navigation}
        </Box>
      )}
      {!navigation && (
        <Box
          sx={{
            minWidth: 0,
            ...(mobileProfile && { textAlign: { xs: "center", sm: "left" } }),
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              minWidth: 0,
              ...(mobileProfile && {
                justifyContent: { xs: "center", sm: "flex-start" },
              }),
            }}
          >
            <Typography
              component="h1"
              noWrap
              title={title}
              sx={{ fontSize: "1rem", fontWeight: 600, lineHeight: 1.3 }}
            >
              {title}
            </Typography>
            {titleAdornment}
          </Box>
          {description && (
            <Typography
              noWrap
              sx={{ color: "text.secondary", fontSize: "0.8125rem" }}
            >
              {description}
            </Typography>
          )}
        </Box>
      )}
      {navigation && (
        <Typography
          component="h1"
          sx={{
            position: "absolute",
            width: "1px",
            height: "1px",
            overflow: "hidden",
            clipPath: "inset(50%)",
          }}
        >
          {title}
        </Typography>
      )}
    </Box>
    {search && (
      <Box
        sx={{
          minWidth: 0,
          gridRow: { xs: 2, lg: 1 },
          gridColumn: { xs: "1 / -1", lg: 2 },
        }}
      >
        {search}
      </Box>
    )}
    {metrics && (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            sm: "repeat(4, auto)",
          },
          columnGap: { xs: 0, sm: 1, lg: 2.5 },
          rowGap: 1.5,
          width: { xs: "100%", sm: "auto" },
          pb: { xs: 1, sm: 0 },
          "& > *": {
            justifyContent: { xs: "center", sm: "flex-start" },
          },
          "& > :nth-of-type(even)": {
            pl: { xs: 0, sm: 1, lg: 2.5 },
            borderLeft: 1,
            borderColor: "divider",
          },
          "& > :nth-of-type(3)": {
            pl: { sm: 1, lg: 2.5 },
            borderLeft: { sm: 1 },
            borderColor: { sm: "divider" },
          },
        }}
      >
        {metrics}
      </Box>
    )}
    {actions && (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          minWidth: 0,
          width: search ? "auto" : { xs: "100%", sm: "auto" },
          ...(inlineMobileActions && { width: "auto" }),
          gridRow: 1,
          gridColumn: { xs: 2, lg: 3 },
          justifySelf: "end",
        }}
      >
        {actions}
      </Box>
    )}
  </Box>
);

interface DisclosureSummaryProps {
  count: number;
  latest: string;
  pageScoped?: boolean;
}
export const DisclosureSummary = ({
  count,
  latest,
  pageScoped = false,
}: DisclosureSummaryProps) => {
  const latestDate = dayjs(latest).isValid()
    ? dayjs(latest).format("MMMM D, YYYY")
    : "—";
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        width: { xs: "100%", sm: "auto" },
        justifyContent: { xs: "space-between", sm: "flex-start" },
        columnGap: 2,
        rowGap: 0.5,
        fontSize: "0.875rem",
        fontVariantNumeric: "tabular-nums",
        color: "text.primary",
      }}
    >
      <Box component="span" sx={{ whiteSpace: "nowrap" }}>
        <Box component="span" sx={{ fontWeight: 600 }}>
          {count.toLocaleString()}
        </Box>{" "}
        {!pageScoped && count === 1 && "disclosure"}
        {!pageScoped && count !== 1 && "disclosures"}
        {pageScoped && "on this page"}
      </Box>
      {count > 0 && (
        <Box
          component="span"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.25,
            borderLeft: { xs: 0, sm: 1 },
            borderColor: "divider",
            pl: { xs: 0, sm: 2 },
            ml: { xs: "auto", sm: 0 },
            textAlign: { xs: "right", sm: "left" },
            whiteSpace: "nowrap",
          }}
        >
          <Box
            component="span"
            sx={{ color: "text.secondary", fontSize: "0.75rem" }}
          >
            Latest
          </Box>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {latestDate}
          </Box>
        </Box>
      )}
    </Box>
  );
};

interface SectionTitleProps {
  children: React.ReactNode;
  count?: number;
}
export const SectionTitle = ({ children, count }: SectionTitleProps) => (
  <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
    <Typography
      component="h2"
      sx={{
        ...panelTitleSx,
        fontSize: "0.875rem",
        color: "text.primary",
        borderLeft: 2,
        borderColor: "primary.main",
        pl: 1,
        lineHeight: 1.5,
      }}
    >
      {children}
    </Typography>
    {count !== undefined && (
      <Typography
        component="span"
        sx={{
          fontSize: "0.6875rem",
          color: "text.secondary",
          bgcolor: "action.hover",
          px: 0.75,
          py: 0.25,
          borderRadius: 0.75,
        }}
      >
        {count.toLocaleString()}
      </Typography>
    )}
  </Box>
);

interface WorkspaceStateProps {
  loading?: boolean;
  message?: string;
  retry?: () => void;
}
export const WorkspaceState = ({
  loading,
  message,
  retry,
}: WorkspaceStateProps) => {
  const theme = useTheme();
  return (
    <Box
      role={retry ? "alert" : "status"}
      sx={{ ...roundedPanelSx(theme.palette.mode === "dark"), py: 5 }}
    >
      {loading && (
        <Box aria-label="Loading data">
          <Skeleton width="30%" />
          <Skeleton height={56} />
          <Skeleton height={56} />
          <Skeleton height={56} />
        </Box>
      )}
      {!loading && (
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            {message}
          </Typography>
          {retry && (
            <Button onClick={retry} sx={{ mt: 1, minHeight: 44 }}>
              Try again
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

interface WorkspaceMetricProps {
  label: string;
  value: React.ReactNode;
  primary?: boolean;
}
export const WorkspaceMetric = ({
  label,
  value,
  primary,
}: WorkspaceMetricProps) => (
  <Box
    sx={{
      minWidth: 0,
      display: "flex",
      alignItems: "center",
      columnGap: 1,
      rowGap: 0.25,
      flexWrap: "wrap",
    }}
  >
    <Typography
      sx={{
        fontSize: "0.8125rem",
        color: "text.secondary",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: primary ? "1rem" : "0.875rem",
        fontWeight: primary ? 700 : 600,
        letterSpacing: "-0.025em",
        lineHeight: 1.2,
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </Typography>
  </Box>
);

interface ChamberControlProps {
  value: string;
  onChange: (value: "all" | "house" | "senate") => void;
  includeAll?: boolean;
}
export const ChamberControl = ({
  value,
  onChange,
  includeAll,
}: ChamberControlProps) => (
  <ToggleButtonGroup
    value={value}
    exclusive
    aria-label="Congressional chamber"
    onChange={(_, next) => {
      if (next) onChange(next);
    }}
    sx={workspaceSwitchSx}
  >
    {includeAll && <ToggleButton value="all">All</ToggleButton>}
    <ToggleButton value="house">House</ToggleButton>
    <ToggleButton value="senate">Senate</ToggleButton>
  </ToggleButtonGroup>
);

interface DisclosurePaginationProps {
  page: number;
  pageSize: number;
  count: number;
  total?: number;
  hasNext: boolean;
  busy?: boolean;
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;
}
export const DisclosurePagination = ({
  page,
  pageSize,
  count,
  total,
  hasNext,
  busy,
  onPage,
  onPageSize,
}: DisclosurePaginationProps) => {
  const paginationRef = useRef<HTMLElement>(null);
  const returnToRows = () => {
    paginationRef.current
      ?.closest("[data-disclosure-panel]")
      ?.scrollIntoView?.({ block: "start" });
  };
  const start = count > 0 ? page * pageSize + 1 : 0;
  const end = count > 0 ? page * pageSize + count : 0;
  return (
    <Box
      component="nav"
      ref={paginationRef}
      aria-label="Disclosure pages"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 1,
        pt: 1.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {start}–{end}
          {total !== undefined && (
            <Box
              component="span"
              sx={{ display: { xs: "none", sm: "inline" } }}
            >
              {" "}
              of {total.toLocaleString()}
            </Box>
          )}
        </Typography>
        <Select
          size="small"
          value={pageSize}
          disabled={busy}
          onChange={(event) => {
            returnToRows();
            onPageSize(Number(event.target.value));
          }}
          inputProps={{ "aria-label": "Disclosures per page" }}
          sx={{
            fontSize: "0.75rem",
            borderRadius: 1,
            "& .MuiSelect-select": { py: 1 },
          }}
        >
          {[25, 50, 100].map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <IconButton
          aria-label="Previous disclosure page"
          disabled={page === 0 || busy}
          onClick={() => {
            returnToRows();
            onPage(page - 1);
          }}
          sx={{ width: 44, height: 44 }}
        >
          <ChevronLeft size={18} />
        </IconButton>
        <Typography variant="caption" color="text.secondary">
          Page {page + 1}
        </Typography>
        <IconButton
          aria-label="Next disclosure page"
          disabled={!hasNext || busy}
          onClick={() => {
            returnToRows();
            onPage(page + 1);
          }}
          sx={{ width: 44, height: 44 }}
        >
          <ChevronRight size={18} />
        </IconButton>
      </Box>
    </Box>
  );
};
